import React, { useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Key, Mail, Save, Eye, EyeOff } from "lucide-react";

const formatErr = (d) => {
  if (!d) return "Erreur";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ") || "Erreur";
  return d?.msg || "Erreur";
};

export default function Settings() {
  const { user } = useAuth();

  // Password form
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  // Recovery email
  const [recoveryEmail, setRecoveryEmail] = useState(user?.recovery_email || "");
  const [savingEmail, setSavingEmail] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) {
      toast.error("Le nouveau mot de passe doit faire au moins 8 caractères.");
      return;
    }
    if (newPw !== confirmPw) {
      toast.error("Les mots de passe ne correspondent pas.");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Mot de passe mis à jour.");
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setSavingPw(false);
    }
  };

  const submitRecovery = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      await api.put("/auth/recovery-email", { recovery_email: recoveryEmail.trim() });
      toast.success("Email de récupération mis à jour.");
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setSavingEmail(false);
    }
  };

  return (
    <div data-testid="settings-page" className="space-y-8 max-w-2xl">
      <div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">
          Réglages
        </h1>
        <p className="text-[#5C665E] mt-2">
          Sécurité de votre compte et préférences.
        </p>
      </div>

      {/* Account info */}
      <section className="rounded-2xl border bg-white p-6" style={{ borderColor: "#E5E2DC" }}>
        <h2 className="font-heading font-bold text-lg mb-4">Compte</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#5C665E] mb-1">Email de connexion</div>
            <div className="font-medium" data-testid="settings-account-email">{user?.email}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#5C665E] mb-1">Rôle</div>
            <div className="font-medium">{user?.role || "admin"}</div>
          </div>
        </div>
      </section>

      {/* Change password */}
      <section className="rounded-2xl border bg-white p-6" style={{ borderColor: "#E5E2DC" }}>
        <h2 className="font-heading font-bold text-lg mb-1">Changer de mot de passe</h2>
        <p className="text-xs text-[#5C665E] mb-4">Au moins 8 caractères.</p>
        <form onSubmit={submitPassword} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Mot de passe actuel</Label>
            <div className="relative">
              <Input
                data-testid="settings-current-pw"
                type={showPw ? "text" : "password"}
                required
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="bg-white border-[#E5E2DC] pr-10"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C665E]"
                tabIndex={-1}
              >
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Nouveau</Label>
              <Input
                data-testid="settings-new-pw"
                type={showPw ? "text" : "password"}
                required
                minLength={8}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="bg-white border-[#E5E2DC]"
                autoComplete="new-password"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Confirmer</Label>
              <Input
                data-testid="settings-confirm-pw"
                type={showPw ? "text" : "password"}
                required
                minLength={8}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="bg-white border-[#E5E2DC]"
                autoComplete="new-password"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="settings-save-pw"
              disabled={savingPw}
              className="rounded-full px-5 py-2.5 text-sm font-semibold text-white btn-lift focus-brand flex items-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: "#2C5545" }}
            >
              <Key size={14} /> {savingPw ? "Enregistrement..." : "Changer le mot de passe"}
            </button>
          </div>
        </form>
      </section>

      {/* Recovery email */}
      <section className="rounded-2xl border bg-white p-6" style={{ borderColor: "#E5E2DC" }}>
        <h2 className="font-heading font-bold text-lg mb-1">Email de récupération</h2>
        <p className="text-xs text-[#5C665E] mb-4">
          Adresse qui recevra les liens de réinitialisation si vous oubliez votre mot de passe.
        </p>
        <form onSubmit={submitRecovery} className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5C665E] mb-1.5 block">Email de récupération</Label>
            <Input
              data-testid="settings-recovery-email"
              type="email"
              required
              value={recoveryEmail}
              onChange={(e) => setRecoveryEmail(e.target.value)}
              placeholder="vous@exemple.fr"
              className="bg-white border-[#E5E2DC]"
              autoComplete="email"
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              data-testid="settings-save-email"
              disabled={savingEmail}
              className="rounded-full px-5 py-2.5 text-sm font-semibold text-white btn-lift focus-brand flex items-center gap-2 disabled:opacity-60"
              style={{ backgroundColor: "#2C5545" }}
            >
              <Mail size={14} /> {savingEmail ? "Enregistrement..." : "Enregistrer l'email"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
